/**
 * `<RestoreVersionConfirm>` — Confirm modal for restoring a saved version.
 *
 * conversation-model-versioning Phase 5 Session 4 (2026-05-17).
 * ADR 0028 (2026-05-18): restore is now DESTRUCTIVE — no safety snapshot is
 * auto-created. The modal shows an explicit destructive warning + a secondary
 * "Create a version of your current state first" escape hatch.
 *
 * Wraps `<ConfirmModal>` per research/phase-5.md §4 + ADR 0028. Body copy
 * follows the user-facing-copy rule — no "agent_state"/"safety"/"frozen
 * state"/"snapshot"/"pointer" naming.
 *
 * `danger` IS set: restore is now irreversible (ADR 0028).
 *
 * Secondary action ABOVE Cancel/Restore: "Create a version of your current
 * state first" — calls `useCreateVersion`. Disabled when the model is not
 * version-ready (same completeness gate as ManageVersionsButton). On success
 * shows inline "Saved as {label}." and leaves the modal open.
 *
 * On successful RESTORE: dispatches `arcanum:restore-analyze` before closing
 * so the builder route fires a synthesized re-analysis turn (ADR 0028).
 *
 * States:
 * - Idle (open):      destructive warning copy + secondary save-first button.
 * - Submitting:       "Restoring…" label on the confirm button.
 * - Error (409 RUN_IN_PROGRESS): inline "Finish or stop the current run
 *                     before restoring." No internal-flow nouns.
 * - Success:          dispatches arcanum:restore-analyze + closes promptly.
 */
import { useState } from 'react';
import clsx from 'clsx';
import { ConfirmModal } from '@/components/ui/ConfirmModal';
import {
  useRestoreVersion,
  useCreateVersion,
} from '@/hooks/use-conversation-versions';
import { ApiError } from '@/lib/api';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface RestoreVersionConfirmProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called to close the modal (Escape / Cancel). NOT called on success — use onRestoreSuccess. */
  onClose: () => void;
  /**
   * Called after a successful restore, just before the modal closes.
   * Allows the parent (BrowseVersionsModal) to close itself as well.
   * Optional — defaults to the same behavior as onClose.
   */
  onRestoreSuccess?: () => void;
  /** Active conversation id — threaded to the restore mutation. */
  conversationId: string | null;
  /** The id of the version to restore. */
  versionId: string | null;
  /** Human-readable label for the version (e.g. "v2" or "Baseline"). */
  versionLabel: string;
  /**
   * Whether the current model state is version-ready (completeness gate).
   * When false, the "Create a version first" button is disabled.
   */
  versionReady: boolean;
  /**
   * Tooltip copy for the disabled state of the "Create a version first"
   * button. Should read "Address these inputs first: …"
   */
  blockingReason: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RestoreVersionConfirm({
  open,
  onClose,
  onRestoreSuccess,
  conversationId,
  versionId,
  versionLabel,
  versionReady,
  blockingReason,
}: RestoreVersionConfirmProps) {
  const restore = useRestoreVersion(conversationId);
  const createVersion = useCreateVersion(conversationId);

  // Track the inline "saved as" confirmation after a successful pre-save.
  const [savedLabel, setSavedLabel] = useState<string | null>(null);

  function handleConfirm() {
    if (!versionId || restore.isPending) return;
    restore.mutate(
      { versionId },
      {
        onSuccess: () => {
          // Dispatch the auto-analysis bridge event BEFORE closing.
          if (typeof window !== 'undefined') {
            try {
              window.dispatchEvent(
                new CustomEvent('arcanum:restore-analyze', {
                  detail: { conversationId, label: versionLabel },
                })
              );
            } catch {
              // Defensive — CustomEvent unsupported envs shouldn't crash.
            }
          }
          resetState();
          // Call onRestoreSuccess (which also closes the parent BrowseVersionsModal)
          // if provided, otherwise fall back to the plain onClose.
          if (onRestoreSuccess) {
            onRestoreSuccess();
          } else {
            onClose();
          }
        },
      }
    );
  }

  function handleCreateFirst() {
    if (!versionReady || createVersion.isPending) return;
    createVersion.mutate(
      {},
      {
        onSuccess: (data) => {
          setSavedLabel(data.label);
        },
      }
    );
  }

  function resetState() {
    restore.reset();
    createVersion.reset();
    setSavedLabel(null);
  }

  function handleClose() {
    if (restore.isPending) return;
    resetState();
    onClose();
  }

  // 409 RUN_IN_PROGRESS — user-facing copy; no internal-flow nouns.
  const isRunInProgress =
    restore.isError &&
    restore.error instanceof ApiError &&
    restore.error.status === 409;

  const confirmLabel = restore.isPending ? 'Restoring…' : 'Restore';
  const createFirstDisabled = !versionReady || createVersion.isPending;

  return (
    <ConfirmModal
      open={open}
      onClose={handleClose}
      onConfirm={handleConfirm}
      title="Restore version"
      confirmLabel={confirmLabel}
      danger
    >
      <div
        className="flex flex-col gap-3"
        data-testid="restore-version-confirm-body"
      >
        {/* Destructive warning copy */}
        <p className="text-sm font-body text-text leading-relaxed">
          Restoring will replace your current working state with{' '}
          <span className="font-mono font-semibold text-text-bright">
            {versionLabel}
          </span>
          . This cannot be undone.
        </p>

        {/* Secondary action: save current state first */}
        <div className="flex flex-col gap-1.5">
          <button
            type="button"
            onClick={handleCreateFirst}
            disabled={createFirstDisabled}
            title={
              !versionReady
                ? blockingReason
                : 'Save your current model state as a version before restoring.'
            }
            data-testid="restore-version-save-first"
            className={clsx(
              'self-start font-mono text-xs uppercase tracking-[0.5px] px-2 py-1 border rounded-[2px] transition-colors',
              createFirstDisabled
                ? 'text-text-dim border-panel-border cursor-not-allowed opacity-50'
                : 'text-accent border-accent/40 hover:bg-accent/10 cursor-pointer'
            )}
          >
            {createVersion.isPending
              ? 'Saving…'
              : 'Create a version of your current state first'}
          </button>

          {/* Inline saved confirmation */}
          {savedLabel && (
            <p
              className="text-xs font-body text-accent"
              data-testid="restore-version-save-first-success"
            >
              Saved as{' '}
              <span className="font-mono font-semibold">{savedLabel}</span>.
            </p>
          )}

          {/* Inline create-first error */}
          {createVersion.isError && !savedLabel && (
            <p
              className="text-xs font-body text-danger"
              data-testid="restore-version-save-first-error"
            >
              {createVersion.error instanceof Error
                ? createVersion.error.message
                : 'Could not save current state. Try again.'}
            </p>
          )}
        </div>

        {/* Error — 409 RUN_IN_PROGRESS */}
        {isRunInProgress && (
          <p
            className="text-xs font-body text-danger"
            data-testid="restore-version-run-in-progress"
          >
            Finish or stop the current run before restoring.
          </p>
        )}

        {/* Generic mutation error (non-409) */}
        {restore.isError && !isRunInProgress && (
          <p
            className="text-xs font-body text-danger"
            data-testid="restore-version-error"
          >
            {restore.error instanceof Error
              ? restore.error.message
              : 'Something went wrong. Please try again.'}
          </p>
        )}
      </div>
    </ConfirmModal>
  );
}
